/**
 * Player State Selectors
 */

import { extractNodeFromConnection } from '../../utils/relay';
import { getTrackIdFromProps, IPropsWithTrackId } from '../../utils/track';
import { getTrackDetailsTrackInfo, getUIState, trackDetailsPanelIsVisible } from './ui';

import { memoize, some } from 'lodash';
import { createSelector } from 'reselect';

export type CurrentTrackProps = Pick<ICurrentPlayerTrack, 'trackId' | 'versionId'>;
export type TrackInfoSelector = (trackId: string) => GQL.ITrackSearchResult | null;

const defaultList = [];

export const getPlayerState = ({ player }: IStoreState) => player;
export const getQueue = ({ queue }: IPlayerState) => queue;

export const getCurrentTrack = createSelector(getPlayerState, ({ currentTrack }) => currentTrack);
export const getIsTrackPlaying = createSelector(getPlayerState, ({ isTrackPlaying }) => isTrackPlaying);

export const makeIsTrackPlaying = () =>
  createSelector<IStoreState, IPropsWithTrackId, IPlayerState, string | undefined, boolean>(
    [getPlayerState, getTrackIdFromProps],
    ({ currentTrack, isTrackPlaying }, trackId) => {
      return isTrackPlaying && !!currentTrack && (
        currentTrack.trackId === trackId
        || currentTrack.versionId === trackId
      );
    },
  );

export const makeIsTrackListened = () =>
  createSelector<IStoreState, IPropsWithTrackId, IPlayerState, string | undefined, boolean>(
    [getPlayerState, getTrackIdFromProps],
    ({ history }, trackId) => {
      return some(history, { versionId: trackId });
    },
  );

export const getTermsForCurrentlyPlayingTrack = createSelector(
  getPlayerState,
  ({ currentTrack }) => {
    return currentTrack && currentTrack.trackInfo.terms ?
      extractNodeFromConnection<GQL.ITaxonomyTerm>(currentTrack.trackInfo.terms)
      : defaultList;
  },
);

export const isTermListOpen = createSelector(
  getUIState,
  ({ player }) => !!player && !!player.termListIsVisible,
);

export const isTrackDetailsOpen = createSelector(
  [trackDetailsPanelIsVisible, getTrackDetailsTrackInfo, getPlayerState],
  (isVisible, trackInfo, { currentTrack }) => {
    return isVisible && !!trackInfo && !!currentTrack && trackInfo.id === currentTrack.trackId;
  },
);

export const getPlayerTrack = createSelector<IStoreState, IPlayerState, CurrentTrackProps | null>(
  getPlayerState,
  ({ currentTrack }) => !!currentTrack ?
    {
      trackId: currentTrack.trackId,
      versionId: currentTrack.versionId,
    } : null,
);

export const getPlayerQueue = createSelector(
  getPlayerState,
  ({ queue }) => queue,
);

export const getTrackInfoFromQueue = createSelector(
  getPlayerQueue,
  (queue) => memoize<TrackInfoSelector>((id: string) => {
    const track = queue.find(({ trackId, versionId }) => trackId === id || versionId === id);
    if (track) {
      return track.trackInfo;
    }

    return null;
  }),
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/redux/selectors/player.ts